import { ChangeDetectionStrategy, Component, input, computed, inject, signal, OnInit } from '@angular/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { ProjelerService, Proje } from './projeler.service';
import { NzInputModule } from 'ng-zorro-antd/input';

const DURUM_RENK: Record<string, string> = {
  'planlama': 'blue', 'devam-ediyor': 'green', 'tamamlandi': 'default',
  'beklemede': 'orange', 'iptal': 'red',
};
const DURUM_ETIKET: Record<string, string> = {
  'planlama': 'Planlama', 'devam-ediyor': 'Devam Ediyor', 'tamamlandi': 'Tamamlandı',
  'beklemede': 'Beklemede', 'iptal': 'İptal',
};
const ONCELIK_RENK: Record<string, string> = {
  'dusuk': 'cyan', 'normal': 'blue', 'yuksek': 'gold', 'acil': 'red',
};
const ONCELIK_ETIKET: Record<string, string> = {
  'dusuk': 'Düşük', 'normal': 'Normal', 'yuksek': 'Yüksek', 'acil': 'Kritik',
};

@Component({
  selector: 'projeler-sayfa',
  standalone: true,
  imports: [NzCardModule, NzTagModule, NzProgressModule, NzDescriptionsModule, NzStatisticModule, NzDividerModule, NzSpinModule,
    NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="prj-sayfa">
      <!-- Baslik -->
      <div class="prj-sayfa__baslik-bar">
        <h2 class="prj-sayfa__baslik">{{ p()?.projeAdi || 'Proje Detayı' }}</h2>
        <div class="prj-sayfa__etiketler">
          <nz-tag [nzColor]="durumRenk()">{{ durumEtiket() }}</nz-tag>
          <nz-tag [nzColor]="oncelikRenk()">{{ oncelikEtiket() }}</nz-tag>
        </div>
      </div>

      <!-- Istatistik Kartlari -->
      <div class="prj-sayfa__istatistikler">
        <nz-card nzBorderless class="prj-sayfa__stat-kart">
          <nz-statistic nzTitle="İlerleme" [nzValue]="(p()?.yuzdeTamamlanma || 0) + ''" nzSuffix="%"></nz-statistic>
          <nz-progress
            [nzPercent]="p()?.yuzdeTamamlanma || 0"
            [nzShowInfo]="false"
            nzSize="small"
            [nzStrokeColor]="ilerlemeRenk()">
          </nz-progress>
        </nz-card>

        <nz-card nzBorderless class="prj-sayfa__stat-kart">
          <nz-statistic nzTitle="Bütçe" [nzValue]="butceStr()" nzSuffix="₺"></nz-statistic>
          @if (p()?.butce) {
            <div class="prj-sayfa__harcama">
              Harcama: {{ (p()?.harcama || 0) | number:'1.0-0' }} ₺
            </div>
          }
        </nz-card>

        <nz-card nzBorderless class="prj-sayfa__stat-kart">
          <nz-statistic nzTitle="Toplam Süre" [nzValue]="toplamGunStr()"></nz-statistic>
        </nz-card>
      </div>

      <nz-divider></nz-divider>

      <!-- Detay -->
      <nz-card nzTitle="Detaylar" nzBorderless>
        <nz-descriptions nzBordered [nzColumn]="{ xs: 1, sm: 2 }">
          <nz-descriptions-item nzTitle="Açıklama" [nzSpan]="2">
            {{ p()?.aciklama || '—' }}
          </nz-descriptions-item>
          <nz-descriptions-item nzTitle="Başlangıç Tarihi">
            {{ tarihFmt(p()?.baslangicTarihi) }}
          </nz-descriptions-item>
          <nz-descriptions-item nzTitle="Bitiş Tarihi">
            {{ tarihFmt(p()?.bitisTarihi) }}
          </nz-descriptions-item>
          <nz-descriptions-item nzTitle="Sorumlu">
            {{ p()?.sorumluKisi || '—' }}
          </nz-descriptions-item>
          <nz-descriptions-item nzTitle="Müşteri / Firma">
            {{ p()?.musteriFirma || '—' }}
          </nz-descriptions-item>
        </nz-descriptions>
      </nz-card>

      <!-- Ekip -->
      @if (p()?.ekipUyeleri?.length) {
        <nz-card nzTitle="Ekip Üyeleri" nzBorderless class="prj-sayfa__ekip">
          <div class="prj-sayfa__ekip-liste">
            @for (uye of p().ekipUyeleri; track uye) {
              <nz-tag>{{ uye }}</nz-tag>
            }
          </div>
        </nz-card>
      }

      <!-- Notlar -->
      @if (p()?.notlar) {
        <nz-card nzTitle="Notlar" nzBorderless>
          <p class="prj-sayfa__notlar">{{ p().notlar }}</p>
        </nz-card>
      }
    </div>
  `,
  styles: [`
    .prj-sayfa {
      display: flex;
      flex-direction: column;
      gap: 16px;
      max-width: 860px;
      margin: 0 auto;
      animation: prjFadeIn 0.35s ease;
    }
    @keyframes prjFadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .prj-sayfa__baslik-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }
    .prj-sayfa__baslik {
      margin: 0;
      font-size: 22px;
      font-weight: 700;
      color: var(--py-metin, #222);
    }
    .prj-sayfa__etiketler {
      display: flex;
      gap: 6px;
    }
    .prj-sayfa__istatistikler {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 12px;
    }
    .prj-sayfa__stat-kart {
      background: var(--py-arkaplan-ikincil, #fafafa);
      border-radius: 10px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .prj-sayfa__stat-kart:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0,0,0,.08);
    }
    .prj-sayfa__harcama {
      font-size: 12px;
      opacity: 0.65;
      margin-top: 4px;
    }
    .prj-sayfa__ekip-liste {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .prj-sayfa__notlar {
      margin: 0;
      line-height: 1.7;
      color: var(--py-metin, #555);
      white-space: pre-wrap;
    }
    nz-card {
      border-radius: 10px;
    }
  `],
})
export class ProjelerSayfaComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  private projelerServis = inject(ProjelerService);

  constructor() { super(); }

  p = computed(() => this.parametreler());

  durumRenk = computed(() => DURUM_RENK[this.p()?.durum] || 'default');
  durumEtiket = computed(() => DURUM_ETIKET[this.p()?.durum] || 'Bilinmiyor');
  oncelikRenk = computed(() => ONCELIK_RENK[this.p()?.oncelik] || 'blue');
  oncelikEtiket = computed(() => ONCELIK_ETIKET[this.p()?.oncelik] || 'Normal');

  ilerlemeRenk = computed(() => {
    const yuzde = this.p()?.yuzdeTamamlanma || 0;
    if (yuzde >= 100) return '#52c41a';
    if (yuzde >= 60) return '#1890ff';
    if (yuzde >= 30) return '#fa8c16';
    return '#f5222d';
  });

  butceStr = computed(() => {
    const b = this.p()?.butce;
    return b ? b.toLocaleString('tr-TR') : '0';
  });

  toplamGunStr = computed(() => {
    const p = this.p();
    if (!p?.baslangicTarihi || !p?.bitisTarihi) return '—';
    const bas = new Date(p.baslangicTarihi).getTime();
    const bit = new Date(p.bitisTarihi).getTime();
    const gun = Math.ceil((bit - bas) / 86400000);
    return gun > 0 ? `${gun} gün` : '—';
  });

  tarihFmt(iso?: string): string {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return '—'; }
  }
}
